import { pipe, prop } from 'ramda';
import styled, { IStyledComponent } from 'styled-components';
import { SCHelper, StyledComponentProps } from '../../theme';
import { tvar } from '@versita/frontend/design-tokens';
import { ButtonProps } from './Button';
import { Link, LinkProps } from 'react-router-dom';
import { Substitute } from 'styled-components/dist/types';

export const enum ButtonStyle {
  Primary = 'PRIMARY',
  Secondary = 'SECONDARY',
}

export const enum ButtonSize {
  Small = 'SMALL',
  Standard = 'STANDARD',
}

const getButtonStyles: SCHelper<ButtonProps> = ({
  disabled,
  loading,
  $buttonStyle,
  theme,
}) => {
  if (disabled || loading) {
    return theme.colors.button.disabled;
  }
  if ($buttonStyle === ButtonStyle.Secondary) {
    return theme.colors.button.secondary;
  }
  return theme.colors.button.primary;
};

const getButtonStyle = (key: string) => pipe(getButtonStyles, prop(key));

export const buttonSpacing = {
  [ButtonSize.Small]: ['single', 'double'],
  [ButtonSize.Standard]: ['double', 'quadruple'],
} as const;

export const getButtonSpacing =
  (axis: 0 | 1) =>
  ({
    theme,
    $buttonSize = ButtonSize.Standard,
  }: StyledComponentProps<{ $buttonSize?: ButtonSize }>) =>
    theme.space[buttonSpacing[$buttonSize][axis]];

export const StyledButton = styled.button<ButtonProps>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${getButtonStyle('fg')};
  letter-spacing: 0.05rem;
  background: ${getButtonStyle('bg')};
  border: 0.14rem solid ${getButtonStyle('border')};
  font-size: ${tvar('typographies.large.fontSize')};
  font-weight: normal;
  padding: ${getButtonSpacing(0)} ${getButtonSpacing(1)};
  transition:
    0.2s background ease-in-out,
    0.2s border ease-in-out;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  &:not(:last-child) {
    margin-bottom: ${tvar('space.x2')};
  }

  &:hover {
    border-color: ${getButtonStyle('borderHover')};
    background: ${getButtonStyle('bgHover')};
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const LinkButton = styled(StyledButton).attrs({ as: Link as any })`
  display: inline-block;
  text-align: center;
  text-decoration: none;
  align-self: flex-start;
` as IStyledComponent<
  'web',
  Substitute<
    React.DetailedHTMLProps<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      HTMLAnchorElement
    >,
    LinkProps & ButtonProps
  >
>;
